import React from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment
} from '@mui/material';
import { CreateJobDto, JobType, Priority } from '../../services/jobService';
import { jobService } from '../../services/jobService';
import GoogleMapsAutocomplete from './GoogleMapsAutocomplete';

interface JobFormProps {
  formData: CreateJobDto;
  onFormDataChange: (formData: CreateJobDto) => void;
  defaultBounds?: {
    city?: string;
    country?: string;
  };
  onFieldFocus?: () => void;
  showCustomerSelection?: boolean;
  selectedCustomerId?: string;  // Guid from backend
  onCustomerChange?: (customerId: string) => void;  // Guid from backend
}

const JobForm: React.FC<JobFormProps> = ({
  formData,
  onFormDataChange,
  defaultBounds,
  onFieldFocus,
  showCustomerSelection = false,
  selectedCustomerId,
  onCustomerChange
}) => {
  const handleFieldChange = (field: keyof CreateJobDto, value: any) => {
    onFormDataChange({
      ...formData,
      [field]: value
    });
  };

  const handleFieldFocus = () => {
    if (onFieldFocus) {
      onFieldFocus();
    }
  };

  return (
    <Grid item xs={12}>
        <Grid container spacing={2}>
        {showCustomerSelection && (
            <Grid item xs={12}>
            <FormControl fullWidth>
                <InputLabel>Customer</InputLabel>
                <Select
                value={formData.customerId}
                onChange={(e) => {
                    const customerId = e.target.value as string;
                    handleFieldChange('customerId', customerId);
                    if (onCustomerChange) {
                    onCustomerChange(customerId);
                    }
                }}
                onFocus={handleFieldFocus}
                label="Customer"
                >
                {/* Customer options would be passed as props or loaded here */}
                </Select>
            </FormControl>
            </Grid>
        )}
        
        <Grid item xs={12} sx={{ mt: 2 }}>
            <GoogleMapsAutocomplete
            value={formData.jobAddress || ''}
            onChange={(address) => handleFieldChange('jobAddress', address)}
            label="Job Address"
            defaultBounds={defaultBounds}
            />
        </Grid>
        
        <Grid item xs={12} md={6}>
            <FormControl fullWidth>
            <InputLabel>Job Type</InputLabel>
            <Select
                value={formData.type}
                onChange={(e) => handleFieldChange('type', e.target.value as JobType)}
                onFocus={handleFieldFocus}
                label="Job Type"
            >
                {Object.values(JobType).filter(v => typeof v === 'number').map((type) => (
                <MenuItem key={type} value={type}>
                    {jobService.getJobTypeDisplayName(type as JobType)}
                </MenuItem>
                ))}
            </Select>
            </FormControl>
        </Grid>
        
        <Grid item xs={12} md={6}>
            <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
                value={formData.priority}
                onChange={(e) => handleFieldChange('priority', e.target.value as Priority)}
                onFocus={handleFieldFocus}
                label="Priority"
            >
                {Object.values(Priority).filter(v => typeof v === 'number').map((priority) => (
                <MenuItem key={priority} value={priority}>
                    {jobService.getPriorityDisplayName(priority as Priority)}
                </MenuItem>
                ))}
            </Select>
            </FormControl>
        </Grid>
        
        <Grid item xs={12} md={6}>
            <TextField
            fullWidth
            label="Estimated Value"
            type="number"
            value={formData.estimatedValue}
            onChange={(e) => handleFieldChange('estimatedValue', parseFloat(e.target.value) || 0)}
            onFocus={handleFieldFocus}
            InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
            }}
            />
        </Grid>
        
        <Grid item xs={12} md={6}>
            <TextField
            fullWidth
            label="Actual Value"
            type="number"
            value={formData.actualValue || ''}
            onChange={(e) => handleFieldChange('actualValue', parseFloat(e.target.value) || undefined)}
            onFocus={handleFieldFocus}
            InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
            }}
            />
        </Grid>
        
        <Grid item xs={12} md={6}>
            <TextField
            fullWidth
            label="Expected Completion Date"
            type="date"
            value={formData.expectedCompletionDate || ''}
            onChange={(e) => handleFieldChange('expectedCompletionDate', e.target.value)}
            onFocus={handleFieldFocus}
            InputLabelProps={{ shrink: true }}
            />
        </Grid>
        
        <Grid item xs={12}>
            <TextField
            fullWidth
            label="Notes"
            multiline
            rows={3}
            value={formData.notes || ''}
            onChange={(e) => handleFieldChange('notes', e.target.value)}
            onFocus={handleFieldFocus}
            />
        </Grid>
        </Grid>
    </Grid>
  );
};

export default JobForm;
